import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import {
  IThresholdsApiResponse,
  ITransactionThreshold,
} from './transactionThresholdsTypes';

interface IToggleTransactionThresholdInput {
  id: string;
  isActive: boolean;
}

const initialState: IApiState<
  IThresholdsApiResponse<ITransactionThreshold>
> = {
  value: null,
  status: 'idle',
  error: '',
};

export const toggleTransactionThresholdAsync = createAsyncThunk(
  'business-settings/2fa-thresholds/toggle',
  async (
    { id, isActive }: IToggleTransactionThresholdInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.patch<
        IThresholdsApiResponse<ITransactionThreshold>
      >(
        `/business-settings/2fa-thresholds/${id}/toggle`,
        { isActive },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      dispatch(
        showAlert({
          status: 'success',
          description: data?.message ?? '2FA threshold has been updated',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<
        IThresholdsApiResponse<ITransactionThreshold>
      >;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const toggleTransactionThresholdSlice = createSlice({
  name: 'business-settings/2fa-thresholds/toggle',
  initialState,
  reducers: {
    resetToggleTransactionThreshold: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(toggleTransactionThresholdAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(toggleTransactionThresholdAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(toggleTransactionThresholdAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IThresholdsApiResponse<
          ITransactionThreshold
        >;
        state.error = action.error.message;
      });
  },
});

export const { resetToggleTransactionThreshold } =
  toggleTransactionThresholdSlice.actions;

export default toggleTransactionThresholdSlice.reducer;
